/**
 * rPI Porter — Channels Settings (E-102 P2-1, read-only)
 *
 * Renders the per-tenant channel configuration:
 *  - Channel cards (EMAIL / WEBCHAT / CONTACT_FORM) with configured/not-configured state
 *  - Webchat embed <script> snippet (copy-to-clipboard)
 *  - Public inquiry form URL (copy-to-clipboard)
 *
 * SCOPE LOCK — READ-ONLY (ADR-102 v0.3 §D-9 / §6):
 *  - This page lists the current ChatwootAccountMapping state.
 *  - Edit flow (status toggles, inbox re-mapping, Chatwoot Automation push) = P2-1b.
 *  - Gmail OAuth connection flow = P2-2.
 *
 * EXCEPTION TO NN-3 "zero rPI→Chatwoot UI link" RULE:
 *  When a channel is NOT configured, the "Bekötés" CTA opens the Chatwoot admin
 *  UI in a new tab. This is the ONE place in the rPI product where the owner
 *  is permitted to touch the underlying Chatwoot admin surface — documented
 *  deliberately because the P1 provisioning pipeline is not yet self-service.
 *  The exception goes away when P2-1b ships the write-through PATCH endpoint.
 */

import type { ChannelConfig, ChannelType } from '@/api/porter-channels';
import { Button } from '@/components/ui';
import { usePorterChannels } from '@/hooks/use-porter-channels';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';

const TEAL = '#14B8A6';

const CHANNEL_ICON: Record<ChannelType, string> = {
  EMAIL: '📧',
  WEBCHAT: '💬',
  CONTACT_FORM: '📝',
};

// ============================================
// Copy-to-clipboard helper
// ============================================

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard !== undefined && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to fallback
  }
  // Fallback for browsers / test environments without Clipboard API
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

// ============================================
// Subcomponents
// ============================================

function ChannelsSkeleton() {
  return (
    <div data-testid="channels-skeleton" className="space-y-6 animate-pulse">
      <div className="h-8 w-48 bg-slate-200 dark:bg-slate-700 rounded" />
      <div className="grid gap-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 bg-slate-100 dark:bg-slate-800 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

function ChannelsError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="channels-error"
      className="rounded-xl border border-red-300 bg-red-50 dark:bg-red-900/20 p-4"
    >
      <p className="text-red-800 dark:text-red-200">
        {t('porter.settings.channels.error.loadFail')}
      </p>
      <Button onClick={onRetry} className="mt-2">
        {t('porter.settings.channels.error.retry')}
      </Button>
    </div>
  );
}

function NotConfiguredAlert() {
  const { t } = useTranslation();
  return (
    <div
      data-testid="channels-not-configured-alert"
      role="alert"
      className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-4"
    >
      <h2 className="font-semibold text-amber-900 dark:text-amber-200">
        {t('porter.settings.channels.notConfigured.title')}
      </h2>
      <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
        {t('porter.settings.channels.notConfigured.description')}
      </p>
    </div>
  );
}

interface ChannelCardProps {
  channel: ChannelConfig;
}

function ChannelCard({ channel }: ChannelCardProps) {
  const { t } = useTranslation();
  const nameKey = `porter.settings.channels.list.${channel.channelType}.name` as const;
  const descKey = `porter.settings.channels.list.${channel.channelType}.description` as const;

  const onSetupClick = () => {
    // NN-3 EXCEPTION (see file header): linking to Chatwoot admin UI in a new
    // tab is allowed ONLY from the channels settings page, only when the
    // channel is not yet wired. P2-1b removes this exception entirely.
    const chatwootAdminUrl =
      (typeof window !== 'undefined' &&
        (window as unknown as { __CHATWOOT_ADMIN_URL__?: string }).__CHATWOOT_ADMIN_URL__) ||
      'https://chat.mflerp.com';
    window.open(chatwootAdminUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      data-testid={`channel-card-${channel.channelType}`}
      className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 flex items-center gap-4"
    >
      <div
        aria-hidden="true"
        className="text-3xl"
        style={{ color: channel.configured ? TEAL : undefined }}
      >
        {CHANNEL_ICON[channel.channelType]}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-slate-900 dark:text-slate-100 truncate">
            {t(nameKey)}
          </h3>
          {channel.configured ? (
            <span className="inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
              {t('porter.settings.channels.list.configured')}
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full border border-amber-400/40 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
              {t('porter.settings.channels.list.notConfigured')}
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-0.5">{t(descKey)}</p>
        {channel.configured && channel.inboxId !== null ? (
          <p className="text-xs text-muted-foreground mt-1">
            inbox id: <code>{channel.inboxId}</code>
          </p>
        ) : null}
      </div>

      {!channel.configured ? (
        <Button onClick={onSetupClick} data-testid={`channel-setup-cta-${channel.channelType}`}>
          {t('porter.settings.channels.list.setupCta')}
        </Button>
      ) : null}
    </div>
  );
}

interface CopyableProps {
  text: string;
  multiline?: boolean;
  testId: string;
}

function CopyableBlock({ text, multiline, testId }: CopyableProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex items-start gap-2" data-testid={testId}>
      {multiline ? (
        <pre className="flex-1 overflow-x-auto rounded-md bg-slate-100 dark:bg-slate-800 p-3 text-xs text-slate-900 dark:text-slate-100 whitespace-pre">
          {text}
        </pre>
      ) : (
        <input
          readOnly
          value={text}
          className="flex-1 rounded-md border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
          data-testid={`${testId}-input`}
        />
      )}
      <Button onClick={onCopy} data-testid={`${testId}-copy`}>
        {copied ? t('porter.settings.channels.copySuccess') : t('porter.settings.channels.copy')}
      </Button>
    </div>
  );
}

// ============================================
// Page
// ============================================

export function SettingsChannelsPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = usePorterChannels();

  if (isLoading) {
    return <ChannelsSkeleton />;
  }

  if (isError || data === undefined) {
    return <ChannelsError onRetry={() => void refetch()} />;
  }

  const { channels, webchatEmbedSnippet, publicFormUrl, status } = data.data;

  return (
    <div className="space-y-6" data-testid="settings-channels-page">
      <header>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          {t('porter.settings.channels.title')}
        </h1>
        <p className="text-muted-foreground mt-1">{t('porter.settings.channels.subtitle')}</p>
      </header>

      {status === 'NOT_CONFIGURED' ? <NotConfiguredAlert /> : null}

      <section>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">
          {t('porter.settings.channels.list.title')}
        </h2>
        <div className="grid gap-4">
          {channels.map(channel => (
            <ChannelCard key={channel.channelType} channel={channel} />
          ))}
        </div>
      </section>

      {webchatEmbedSnippet !== null ? (
        <section>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
            {t('porter.settings.channels.webchat.title')}
          </h2>
          <p className="text-muted-foreground text-sm mb-3">
            {t('porter.settings.channels.webchat.description')}
          </p>
          <CopyableBlock text={webchatEmbedSnippet} multiline testId="channels-webchat-snippet" />
        </section>
      ) : null}

      <section>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
          {t('porter.settings.channels.form.title')}
        </h2>
        <p className="text-muted-foreground text-sm mb-3">
          {t('porter.settings.channels.form.description')}
        </p>
        <CopyableBlock text={publicFormUrl} testId="channels-public-form-url" />
      </section>
    </div>
  );
}

export default SettingsChannelsPage;
